import { useEffect, useRef, useState } from 'react';
import ListLoading from './ListLoading';

function ScrollPage({ pdfPage, width, viewportRef, onRendered, onError }) {
  const element = useRef(null);
  const canvas = useRef(null);
  const [nearby, setNearby] = useState(() => typeof IntersectionObserver === 'undefined');
  const natural = pdfPage.getViewport({ scale: 1 });
  const scale = width / natural.width;
  const height = natural.height * scale;
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setNearby(entry.isIntersecting), {
      root: viewportRef.current, rootMargin: '600px',
    });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [viewportRef]);
  useEffect(() => {
    if (!nearby) return undefined;
    let active = true;
    const target = canvas.current;
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    const viewport = pdfPage.getViewport({ scale });
    target.width = Math.ceil(viewport.width * ratio);
    target.height = Math.ceil(viewport.height * ratio);
    const context = target.getContext('2d');
    const task = pdfPage.render({ canvasContext: context, viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
    task.promise.then(() => { if (active) onRendered(pdfPage.pageNumber); }).catch(error => {
      if (active && error?.name !== 'RenderingCancelledException') onError();
    });
    return () => { active = false; task.cancel?.(); };
  }, [nearby, pdfPage, scale, onRendered, onError]);
  return <div ref={element} style={{ width, height }} className="relative shrink-0 bg-white shadow-sm" aria-label={`Vertragsseite ${pdfPage.pageNumber}`}>
    {nearby && <canvas ref={canvas} style={{ width, height }} className="block" />}
  </div>;
}

export default function ContinuousPdfPages({ document, width, viewportRef, onRendered, onError }) {
  const [pages, setPages] = useState(null);
  useEffect(() => {
    let active = true;
    // Establish every page's dimensions before scrolling can reach the real document end.
    Promise.all(Array.from({ length: document.numPages }, (_, index) => document.getPage(index + 1)))
      .then(result => { if (active) setPages(result); })
      .catch(() => { if (active) onError(); });
    return () => { active = false; };
  }, [document, onError]);
  if (!pages) return <ListLoading label="Vertrag wird geladen …" compact />;
  return <div className="flex min-w-full w-max flex-col items-center gap-4">
    {pages.map((pdfPage, index) => <ScrollPage key={index} {...{pdfPage, width, viewportRef, onRendered, onError}} />)}
  </div>;
}
